import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, ArrowRight, Loader2, ChevronDown } from "lucide-react";
import { Reveal, SectionLabel, TopWave } from "./shared";
import { useLang } from "./i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-ink outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-slate-400 focus:border-aqua-600 focus:bg-white focus:ring-4 focus:ring-aqua-600/10";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-slate-500">{label}</span>
      {children}
    </label>
  );
}

export function QuoteCTA() {
  const { t } = useLang();
  const empty = { name: "", email: "", phone: "", transaction_type: t.quote.types[0], province: t.quote.provinces[0], message: "" };
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success(t.quote.success);
      setForm(empty);
    } catch (err) {
      toast.error(t.quote.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="quote" className="grain t-bg-deep relative py-24 md:py-36">
      <TopWave fill="rgb(var(--c-deep))" />
      <div aria-hidden="true" className="absolute -left-40 top-1/3 h-[440px] w-[440px] rounded-full bg-aqua-500/15 blur-[130px]" />
      <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-2">
        <Reveal>
          <SectionLabel dark>{t.quote.label}</SectionLabel>
          <h2 className="t-text font-display text-4xl font-semibold leading-[1.08] tracking-[-0.02em] md:text-6xl">
            {t.quote.titleA} <span className="italic text-maple">{t.quote.titleB}</span>
          </h2>
          <p className="t-text-muted mt-6 max-w-md text-base leading-relaxed md:text-lg">{t.quote.sub}</p>
          <ul className="mt-9 space-y-4">
            {t.quote.promises.map((p, i) => (
              <li key={i} className="t-text-soft flex items-center gap-3 text-sm font-semibold">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-maple/15">
                  <Check size={13} strokeWidth={3} className="text-maple" />
                </span>
                {p}
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={0.12}>
          <form
            data-testid="quote-form"
            onSubmit={submit}
            className="rounded-3xl bg-white p-7 shadow-[0_36px_90px_rgba(0,0,0,0.18)] sm:p-9"
          >
            <h3 className="font-display text-2xl font-semibold text-ink">{t.quote.formTitle}</h3>
            <p className="mt-1.5 text-sm text-slate-400">{t.quote.formSub}</p>
            <div className="mt-7 grid gap-4 sm:grid-cols-2">
              <Field label={t.quote.fields.name}>
                <input data-testid="quote-name-input" required value={form.name} onChange={set("name")} placeholder={t.quote.placeholders.name} className={inputCls} />
              </Field>
              <Field label={t.quote.fields.email}>
                <input data-testid="quote-email-input" required type="email" value={form.email} onChange={set("email")} placeholder={t.quote.placeholders.email} className={inputCls} />
              </Field>
              <Field label={t.quote.fields.phone}>
                <input data-testid="quote-phone-input" value={form.phone} onChange={set("phone")} placeholder={t.quote.placeholders.phone} className={inputCls} />
              </Field>
              <Field label={t.quote.fields.type}>
                <div className="relative">
                  <select data-testid="quote-type-select" value={form.transaction_type} onChange={set("transaction_type")} className={`${inputCls} appearance-none pr-10`}>
                    {t.quote.types.map((ty) => (
                      <option key={ty}>{ty}</option>
                    ))}
                  </select>
                  <ChevronDown size={16} className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                </div>
              </Field>
              <Field label={t.quote.fields.province}>
                <div className="relative">
                  <select data-testid="quote-province-select" value={form.province} onChange={set("province")} className={`${inputCls} appearance-none pr-10`}>
                    {t.quote.provinces.map((p) => (
                      <option key={p}>{p}</option>
                    ))}
                  </select>
                  <ChevronDown size={16} className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                </div>
              </Field>
              <div className="sm:col-span-2">
                <Field label={t.quote.fields.message}>
                  <textarea data-testid="quote-message-input" rows={3} value={form.message} onChange={set("message")} placeholder={t.quote.placeholders.message} className={`${inputCls} resize-none`} />
                </Field>
              </div>
            </div>
            <button
              data-testid="quote-submit-btn"
              type="submit"
              disabled={sending}
              className="group mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-maple py-4 text-sm font-bold text-white transition-colors duration-200 hover:bg-[#C23225] disabled:opacity-60"
            >
              {sending ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> {t.quote.sending}
                </>
              ) : (
                <>
                  {t.quote.submit}
                  <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
                </>
              )}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
